import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Crown, Flame, Star, Trophy, Video, Calendar, MapPin } from 'lucide-react';

export default function FamilyPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/family/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Enlace inválido'));
  }, [token]);
  if (error) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center max-w-md">
        <div className="font-display text-2xl font-extrabold mb-2">Enlace no válido</div>
        <div className="text-slate-500">{error}</div>
      </div>
    </div>
  );
  if (!data) return <div className="p-12 text-slate-500">Cargando…</div>;
  const s = data.student;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-5xl mx-auto p-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-slate-900 grid place-items-center"><Crown className="w-5 h-5 text-amber-300"/></div>
          <div>
            <div className="font-display font-extrabold text-xl">Chess4Kings Academy</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Portal familiar · Solo lectura</div>
          </div>
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white p-8 mb-8 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-amber-500/20 blur-3xl"/>
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Progreso de</div>
            <div className="font-display text-4xl font-extrabold" data-testid="family-child-name">{s.first_name} {s.last_name}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Nivel</div>
                <div className="font-display text-3xl font-extrabold">{s.gamification_level}</div>
              </div>
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Puntos</div>
                <div className="font-display text-3xl font-extrabold flex items-center gap-1"><Star className="w-6 h-6 text-amber-400"/>{s.points}</div>
              </div>
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Racha</div>
                <div className="font-display text-3xl font-extrabold flex items-center gap-1"><Flame className="w-6 h-6 text-orange-400"/>{s.streak_days}</div>
              </div>
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-white/60 font-bold">Rating</div>
                <div className="font-display text-3xl font-extrabold">{s.chess_rating}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Esta semana</div>
            <div className="font-display text-3xl font-extrabold text-blue-700 mt-1">{data.weekly.exercises}</div>
            <div className="text-sm text-slate-500">ejercicios resueltos</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Esta semana</div>
            <div className="font-display text-3xl font-extrabold text-emerald-700 mt-1">{data.weekly.lessons}</div>
            <div className="text-sm text-slate-500">lecciones completadas</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total logros</div>
            <div className="font-display text-3xl font-extrabold text-amber-600 mt-1">{data.achievements.length}</div>
            <div className="text-sm text-slate-500">desbloqueados</div>
          </div>
        </div>

        {data.next_class && (
          <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-8 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 grid place-items-center"><Video className="w-6 h-6"/></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Próxima clase</div>
              <div className="font-display font-bold text-lg">{data.next_class.title}</div>
              <div className="text-sm text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> {new Date(data.next_class.starts_at).toLocaleString()}</div>
            </div>
          </div>
        )}

        <h2 className="font-display font-bold text-2xl mb-4">Logros desbloqueados</h2>
        {data.achievements.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
            Aún no ha desbloqueado logros. ¡Anímalo/a a seguir practicando!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.achievements.map(a => (
              <div key={a.id} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-2xl bg-amber-500 text-white grid place-items-center"><Trophy className="w-5 h-5"/></div>
                  <div>
                    <div className="font-display font-bold">{a.name}</div>
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{a.rarity}</div>
                  </div>
                </div>
                <div className="text-sm text-slate-600">{a.description}</div>
              </div>
            ))}
          </div>
        )}
        <div className="text-center text-xs text-slate-400 mt-12 font-mono">Portal solo lectura · Enlace privado válido 1 año</div>
      </div>
    </div>
  );
}
